import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { DrugLookupService } from './services/drug-lookup.service';
import { DrugAutoCompleteItem } from 'src/app/shared/interfaces/drug-autocomplete-item.interface';
import { debounceTime, tap, switchMap } from 'rxjs/operators';
import { DrugItem } from 'src/app/shared/interfaces/drug-item.interface';
import { DOSAGE_FREQUENCIES_ENUM } from 'src/app/shared/enums/dosage-frequency.enum';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators, FormArray } from '@angular/forms';
import { EnumHelper } from 'src/app/shared/utility-services/enum-helper.class';
import { DrugDosage } from 'src/app/shared/interfaces/drug-dosage.interface';
import { StateStructure } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { of, from } from 'rxjs';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  addMedicationToSwapMap,
  GenericAndBrandObject,
  MedicationStateStructure,
  swapGenericAndBrand,
  removeDrugById,
  SelectedMedication,
  selectedMedsAreValid,
  selectedMedsAreInvalid,
  storeCurrentMedications
} from './reducers/medication.reducer';
import { Package } from 'src/app/shared/interfaces/drug-dosage-package.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PtlWindowLocationService } from '@ptl/global-components';
import { environment } from 'src/environments/environment';
import { plansNeedRefreshedFromBackend } from '../../plan-selection/configs/reducers/plans.reducer';
import { DrxService } from './services/drx.service';
import { DRXStateStructure } from './reducers/drx-response.reducer';

@Component({
    selector: 'app-medication-information',
    templateUrl: './medication-information.component.html',
    styleUrls: ['./medication-information.component.scss'],
    standalone: false
})
export class MedicationInformationComponent implements OnInit {
  @Input() nextButtonUrl: string;
  @Input() backButtonUrl: string;

  /** CONTENT... */
  @Input() componentHeader = 'Find Medications';
  @Input() componentSubTitle = '(Optional)';
  @Input() componentDescription = 'Add any medications you\'re currently taking to the search. This will better refine your results.';
  @Input() autoCompleteSearchLabel = 'Search for Medications';
  @Input() autocompletePlaceholder = 'e.g. Atorvastatin';
  @Input() triggerGenericLabel = 'Generic version';
  @Input() triggerBrandLabel = 'Brand name version';
  @Input() dosageSelectionLabel = 'Dosage';
  @Input() frequencySelectionLabel = 'Frequency';
  @Input() frequencySelectionHint = 'About how often is drug taken';
  @Input() packageSizeSelectionLabel = 'Package Size';
  @Input() packageSizeSelectionError = 'Please make a selection';
  @Input() backButtonLabel = 'Back';
  @Input() nextButtonLabel = 'Next';
  @Input() genericNote;
  /** ...CONTENT */

  autoCompleteLookupResults: Array<DrugAutoCompleteItem>;
  addedDrugs: Array<DrugItem> = [];
  dosageFrequencies: Array<string> = EnumHelper.asStringArray(
    DOSAGE_FREQUENCIES_ENUM
  );

  medicationForm: UntypedFormGroup;
  autocompleteForm: UntypedFormGroup;
  @ViewChild('autoTrigger', { read: MatAutocompleteTrigger, static: true })
  autoTrigger: MatAutocompleteTrigger;
  angularHostUrl: string;

  constructor(
    private drugLookupService: DrugLookupService,
    private fb: UntypedFormBuilder,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private windowLocationService: PtlWindowLocationService,
    private drxService: DrxService
  ) {
    // Build forms
    this.autocompleteForm = this.fb.group({
      userSearch: this.fb.control(null)
    });

    this.medicationForm = this.fb.group({
      selectedMedications: this.fb.array([])
    });

    // Pre-populate Form Array with empties if possible
    let stateDefinedArraySize = 0;
    const currentState = this.reducerUtilityService.getState<MedicationStateStructure>('MedicationInformation');
    if (
      currentState &&
      currentState.medicationForm &&
      currentState.medicationForm.selectedMedications
    ) {
      stateDefinedArraySize =
        currentState.medicationForm.selectedMedications.length;
    }

    for (let i = 0; i < stateDefinedArraySize; i++) {
      (this.medicationForm.get('selectedMedications') as UntypedFormArray).push(
        this.fb.group({
          drug: this.fb.group({
            DrugID: this.fb.control(null),
            DrugName: this.fb.control(null),
            DrugType: this.fb.control(null),
            DrugTypeID: this.fb.control(null),
            ChemicalName: this.fb.control(null),
            GenericDrugID: this.fb.control(null),
            GenericDrugName: this.fb.control(null),
            DrugTypeNDA: this.fb.control(null),
            Dosages: this.fb.control(null)
          }),
          useGenericVersion: this.fb.control(null),
          selectedDosage: this.fb.control(null, Validators.required),
          selectedPackage: this.fb.control(null),
          frequency: this.fb.control(null, Validators.required),
          dosageQuantity: this.fb.control(null, [Validators.required, Validators.min(1)])
        })
      );
    }

  }

  /** Form Control helper */
  get selectedMedications() {
    // return (this.medicationForm.get('selectedMedications') as UntypedFormGroup)
    //   .controls;
    return (this.medicationForm.get('selectedMedications') as FormArray)
  }

  get drugLookupServiceProxy() {
    return this.drugLookupService;
  }

  ngOnInit() {
    this.angularHostUrl = environment.infrastructure.googleCloud.angularHostUrl;

    //Subscribe to changes on user's searched value
    this.autocompleteForm
      .get('userSearch')
      .valueChanges.pipe(
        // Hesitate to do more
        debounceTime(700),
        // Call the service
        switchMap((searchString: string) =>
          searchString.length > 2
            ? from(this.drugLookupService.byName(searchString))
            : of([])
        ),
        // Filter the results before assigning the values
        tap((results: DrugAutoCompleteItem[]) => {
          results.forEach(item => (item.shouldDisplay = true));
          this.autoCompleteLookupResults = results;
          this.filterAutoCompleteResults();
        })
      )
      .subscribe();
  }

  filterAutoCompleteResults() {
    const results = Object.assign([], this.autoCompleteLookupResults);
    
    /** Remove items which have already been selected and match: DrugId and selected DosageId */
    const selectedMedicationsState: MedicationStateStructure
      = this.reducerUtilityService.getState('MedicationInformation');

    const selectedMedications: SelectedMedication[]
      = selectedMedicationsState.medicationForm.selectedMedications;

    if (selectedMedications.length > 0) {
      const filteredResults: DrugAutoCompleteItem[] = Object.assign(
        [],
        results
      );

      /** All */
      // this.autoCompleteLookupResults =
      filteredResults.forEach((autoCompleteDrug: DrugAutoCompleteItem) => {
        /** Get drugs matching result */
        const previouslySelectedDrugsMatchingID = selectedMedications.filter(
          (selectedMedication: SelectedMedication) => {
            return (
              selectedMedication.drug.DrugID === autoCompleteDrug.DrugID
            );
          }
        );

        /** Not previously selected - display */
        if (!previouslySelectedDrugsMatchingID.length) {
          return true;
        }

        /** Pull the Dosages out of any of those results (since they are all the same drug, by ID) */
        const selectedDrugInstanceDosages =
          previouslySelectedDrugsMatchingID[0].drug.Dosages;

        /** Previously selected - but more dosages available - display */
        if (
          selectedDrugInstanceDosages.length >
          previouslySelectedDrugsMatchingID.length
        ) {
          autoCompleteDrug.shouldDisplay = true;
          // return true;
        } else {
          autoCompleteDrug.shouldDisplay = false;
        }

        // return false;
      });

      this.autoCompleteLookupResults = filteredResults;
    }
  }

  /** Attempt to workaround the shadowdom issue of not appropriately handling the panel opening. */
  triggerPanelOpen() {
    this.autoTrigger.openPanel();
  }

  async addDrugToSelected(addedDrug: DrugAutoCompleteItem): Promise<void> {
    this.autocompleteForm.get('userSearch').setValue('');

    const addedDrugFullInfo: DrugItem = await this.drugLookupService.byId(addedDrug.DrugID);

    /** Add to selected */
    (this.medicationForm.get('selectedMedications') as UntypedFormArray).insert(0,
      this.fb.group({
        drug: this.fb.group({
          DrugID: this.fb.control(addedDrugFullInfo.DrugID || null),
          DrugName: this.fb.control(addedDrugFullInfo.DrugName || null),
          DrugType: this.fb.control(addedDrugFullInfo.DrugType || null),
          DrugTypeID: this.fb.control(addedDrugFullInfo.DrugTypeID || null),
          ChemicalName: this.fb.control(
            addedDrugFullInfo.ChemicalName || null
          ),
          GenericDrugID: this.fb.control(
            addedDrugFullInfo.GenericDrugID || null
          ),
          GenericDrugName: this.fb.control(
            addedDrugFullInfo.GenericDrugName || null
          ),
          DrugTypeNDA: this.fb.control(
            addedDrugFullInfo.DrugTypeNDA || null
          ),
          Dosages: this.fb.control(addedDrugFullInfo.Dosages || null)
        }),
        useGenericVersion: this.fb.control(null),
        selectedDosage: this.fb.control(null, Validators.required),
        selectedPackage: this.fb.control(null),
        frequency: this.fb.control(null, Validators.required),
        dosageQuantity: this.fb.control(null, [Validators.required, Validators.min(1)])
      })
    );

    /** If a generic version exists, go grab that as well */
    if (addedDrugFullInfo.GenericDrugID) {
      const genericDrugVariant: DrugItem = await this.drugLookupService.byId(addedDrugFullInfo.GenericDrugID);

      /** Save the mapping between brand and generic to state */
      const genericAndBrand: GenericAndBrandObject = {
        brand: addedDrugFullInfo,
        generic: genericDrugVariant
      };
      /** Update the state with the new mapping */
      this.ngRedux.dispatch(addMedicationToSwapMap(genericAndBrand));
    }
    
    this.filterAutoCompleteResults();
    
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
  }

  /**
   * Return true if:
   * 1) Drug was brand and had a generic
   * 2) Drug was generic, but was previously a branded variant
   *  2a) Mapping would exist at this point
   */
  shouldShowGenericCheckbox(drug: DrugItem): boolean {
    if (drug.GenericDrugID) {
      return true;
    }

    if (drug.DrugTypeNDA === 'Generic') {
      const medicationSelectionState: MedicationStateStructure
        = this.reducerUtilityService.getState<MedicationStateStructure>('MedicationInformation');

      if (medicationSelectionState.brandToGenericMapping.length > 0) {
        return (
          medicationSelectionState.brandToGenericMapping.filter(
            (mappedObj: GenericAndBrandObject) => {
              return mappedObj.brand.GenericDrugID === drug.DrugID;
            }
          ).length > 0
        );
      }
    }

    return false;
  }


  /** Show if Dosage for drug has Package options */
  shouldShowPackageDropdown(
    selectedDosage: string,
    dosagesOptions: DrugDosage[]
  ): boolean {
    if (!selectedDosage || !dosagesOptions) {
      return false;
    }
    const packages: Package[] = this.getPackagesFromDosageByDosageId(
      selectedDosage,
      dosagesOptions
    );
    return packages && packages.length > 0;
  }

  /** Show Packages associated with Selected Dosage */
  whichPackagesToShow(
    selectedDosage: string,
    dosagesOptions: DrugDosage[]
  ): Package[] {
    if (!selectedDosage || !dosagesOptions) {
      return [];
    }

    const packages: Package[] = this.getPackagesFromDosageByDosageId(
      selectedDosage,
      dosagesOptions
    );
    return packages;
  }

  /** Filters and returns appropriate Package options */
  getPackagesFromDosageByDosageId(
    selectedDosage: string,
    dosagesOptions: DrugDosage[]
  ): Package[] {
    const matchingDosages: DrugDosage[] = dosagesOptions.filter(
      (dosageOption: DrugDosage) => dosageOption.DosageID === selectedDosage
    );
    if (matchingDosages) {
      if (matchingDosages[0].Packages) {
        if (matchingDosages[0].Packages.length > 0) {
          return matchingDosages[0].Packages;
        } else {
          console.error('Dosage identified, but packages are empty!');
        }
      }
    }

    return [];
  }

  toggleGeneric(formArray: UntypedFormArray, index: number) {
    // TODO - CLEAN UP REDUCER FUNCTIONS AS WELL
    //this.ngRedux.dispatch(swapGenericAndBrand(drug));
    //this.filterAutoCompleteResults();

    let formControl = formArray.controls[index];
    if (formControl) {
      let drugId = formControl.get('drug').value.DrugID;
      let isUsingGeneric = formControl.get('useGenericVersion').value;
      let medState = this.reducerUtilityService.getState<MedicationStateStructure>('MedicationInformation');
      let mainObj = medState.brandToGenericMapping.filter((obj) => obj.brand.DrugID === drugId || obj.generic.DrugID === drugId)[0];
      this.replaceActiveDrug(formControl, isUsingGeneric ? mainObj.generic : mainObj.brand);
    
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
    }
  }

  replaceActiveDrug(formControl, drugObj: DrugItem) {
    let drugControl = formControl.get('drug');
    drugControl.get('DrugID').setValue(drugObj.DrugID);
    drugControl.get('DrugType').setValue(drugObj.DrugType);
    drugControl.get('DrugTypeID').setValue(drugObj.DrugTypeID);
    drugControl.get('DrugTypeNDA').setValue(drugObj.DrugTypeNDA);
    drugControl.get('Dosages').setValue(drugObj.Dosages);

    // Generic drug name will only show when generic is selected
    // Branded drugs will overwrite this value with their own name, but it will be correct when generic is selected
    drugControl.get('GenericDrugName').setValue(drugObj.DrugName);

    formControl.get('selectedDosage').setValue(null);
    formControl.get('selectedPackage').setValue(null);
    formControl.get('frequency').setValue(null);
    formControl.get('dosageQuantity').setValue(null);
  }

  /**
   * @description - This first removes the data from the state, then waits to remove the actual form item
   * @param drugToRemove - from the form values in the template
   * @param formGroupIndex - used to identify which form group to remove
   */
  removeDrugFromSelected(drugToRemove: DrugItem, formGroupIndex: number): void {
    /** Update State */
    this.ngRedux.dispatch(removeDrugById(drugToRemove.DrugID));
    setTimeout(
      function () {
        (this.medicationForm.get('selectedMedications') as UntypedFormArray).removeAt(
          formGroupIndex
        );

        this.filterAutoCompleteResults();
      }.bind(this),
      0
    );
    
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
  }

  /** Just logs the form */
  submit(form: UntypedFormGroup) {

    /** Checks if the form is valid and updates the state if necessary,
     * This is primarily used for the drx validity
     */
    if (form.valid) {
      this.ngRedux.dispatch(selectedMedsAreValid());
      this.ngRedux.dispatch(storeCurrentMedications());
      // window.location.href = `./${this.nextButtonUrl}`;
      const finalUrl = `${this.nextButtonUrl}`;

      const drxState: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');
      if (this.drxService.hasNewMedsOrPharmacies(drxState.drxPreviousEncodedSessionMeds)) {
        this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
      }

      this.windowLocationService.goToUrl(finalUrl);
    }
    else {
      this.ngRedux.dispatch(selectedMedsAreInvalid());
      form.markAllAsTouched();
    }
  }

  navBack() {
    // window.location.href = `./${this.backButtonUrl}`;
    const finalUrl = `${this.backButtonUrl}`;
    this.windowLocationService.goToUrl(finalUrl);
  }
}
